import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import useRegisterFcmToken from '../hooks/useRegisterFcmToken'; 
import { Capacitor } from '@capacitor/core';



export default function Layout({ children }) {
  useRegisterFcmToken();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnreadBolos, setHasUnreadBolos] = useState(false);
  const [hasReminderPending, setHasReminderPending] = useState(false);
  const [accountSuspended, setAccountSuspended] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);



  const user = auth.currentUser;
  const navigate = useNavigate();
  const sidebarRef = useRef(null);


  const toggleSidebar = () => setIsOpen(prev => !prev);

 const handleLogout = () => {

  // 🧹 Desktop-only: Clear cached notes
  if (!Capacitor.isNativePlatform()) {
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("cachedNotes_") ||
        key.startsWith("lastNotesFetch_"),
        key.startsWith("cachedSharedNotes_") ||
        key.startsWith("lastSharedNotesFetch_") ||
        key.startsWith("filteredNotes_")
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  auth.signOut();
  navigate('/login');
};

  const closeSidebar = () => setIsOpen(false); // Close after clicking a link

  const location = useLocation();
  const hideHamburger =
    location.pathname.startsWith('/direct/') ||
    location.pathname.startsWith('/group/');

useEffect(() => {
  if (location.pathname === '/messages') {
    setHasUnreadMessages(false);
  }
}, [location.pathname]);



useEffect(() => {
  if (!user) return;

  const fetchProfileAndListen = async () => {
    const userDocRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userDocRef);

    if (!userSnap.exists()) {
      console.error('User profile not found in Firestore');
      return;
    }

    const userProfile = userSnap.data();

    setAccountSuspended(!!userProfile.accountSuspended);
    setHasReminderPending(!!userProfile.reminderPending);

    // Removed post-login phone verification enforcement; allow access after email verification only

    const now = new Date();

const q = query(
  collection(db, 'bolos'),
  where('recipients', 'array-contains', userProfile.agency),
  where('expiresAt', '>', now)
);

    const unsubscribe = onSnapshot(q, (snapshot) => {
  let hasUnread = false;

  const lastOpened = parseInt(localStorage.getItem("bolo_last_opened") || "0", 10);

  snapshot.forEach((doc) => {
    const bolo = doc.data();
    const boloTime = bolo.createdAt?.toDate?.().getTime?.() || 0;

    if (boloTime > lastOpened) {
      hasUnread = true;
    }
  });

  setHasUnreadBolos(hasUnread);
});

return unsubscribe;
  };

  const unsubscribePromise = fetchProfileAndListen();

  return () => {
    unsubscribePromise?.then((u) => u && u());
  };
}, [user]);



useEffect(() => {
  if (!user) return;

  const userId = user.uid;
  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('members', 'array-contains', userId)
  );

  const unsubscribe = onSnapshot(
    conversationsQuery,
    (snapshot) => {
      const checkUnreadPromises = snapshot.docs.map(async (docSnap) => {
        try {
          const data = docSnap.data();
          if (data.deleting === true) {
            return false;
          }

          const lastMessageAt = data.lastMessageAt?.toDate?.();
          const lastSender = data.lastMessageSender;
          const lastCallLogAt = data.lastCallLogAt?.toDate?.();
          const lastCallLogType = data.lastCallLogType;
          const lastCallLogBy = data.lastCallLogBy;

          const viewDocRef = doc(db, 'conversations', docSnap.id, 'views', userId);
          const viewDocSnap = await getDoc(viewDocRef);
          const lastViewed = viewDocSnap.exists() ? viewDocSnap.data().lastViewed?.toDate?.() : null;

          const messageUnread =
            lastMessageAt &&
            (!lastViewed || lastMessageAt > lastViewed) &&
            lastSender !== userId;

          const missedCallUnread =
            lastCallLogType === 'missed' &&
            lastCallLogBy &&
            lastCallLogBy !== userId &&
            lastCallLogAt &&
            (!lastViewed || lastCallLogAt > lastViewed);

          return messageUnread || missedCallUnread;
        } catch (error) {
          if (error?.code !== 'permission-denied' && error?.code !== 'not-found') {
            console.error('Unread view lookup failed:', error);
          }
          return false;
        }
      });

      Promise.all(checkUnreadPromises).then((results) => {
        const hasUnread = results.some((r) => r);
        setHasUnreadMessages(hasUnread);
      });
    },
    (error) => {
      console.error('Unread conversations listener error:', error);
      setHasUnreadMessages(false);
    }
  );

  return () => unsubscribe();
}, [user]);


useEffect(() => {
  const handleClickOutside = (event) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  if (isOpen) {
    document.addEventListener('mousedown', handleClickOutside);
  } else {
    document.removeEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [isOpen]);





  return (
<>
 {/* Sidebar with animation */}
      <div
  ref={sidebarRef}
 className={`fixed top-0 left-0 h-full w-40 bg-gray-800 text-white
            z-[2000] p-5 flex flex-col gap-4 transition-transform duration-300
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}

>

       <div className="text-xl font-bold mb-4 ml-8 mt-6">BlueWire</div>

        {user ? (
        <>
    {!accountSuspended && (
      <>
           
            <NavLink
              to="/notes"
              onClick={closeSidebar}
              className={({ isActive }) =>
                isActive
                  ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
                  : 'hover:bg-gray-700 pl-2 py-1'
              }
            >
              Notes
            </NavLink>
	   <NavLink
  to="/fieldbook"
  onClick={closeSidebar}
  className={({ isActive }) =>
    isActive
      ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
      : 'hover:bg-gray-700 pl-2 py-1'
  }
>
  Fieldbook
</NavLink>
	
	<NavLink
  to="/messages"
  onClick={closeSidebar}
  className={({ isActive }) =>
    isActive
      ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1 flex items-center justify-between'
      : 'hover:bg-gray-700 pl-2 py-1 flex items-center justify-between'
  }
>
  <span>Messages</span>
  {hasUnreadMessages && (
    <span
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        backgroundColor: 'gold',
        borderRadius: '50%',
        marginLeft: '8px',
      }}
    />
  )}
</NavLink>


<NavLink
  to="/bolo"
  onClick={() => {
  closeSidebar();
  setHasUnreadBolos(false);
  // Mark all BOLOs as read
  const now = Date.now();
  localStorage.setItem("bolo_last_opened", now.toString());
}}
  className={({ isActive }) =>
    isActive
      ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1 flex items-center justify-between'
      : 'hover:bg-gray-700 pl-2 py-1 flex items-center justify-between'
  }
>
  <span>BOLO</span>
  {hasUnreadBolos && (
    <span
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        backgroundColor: 'gold',
        borderRadius: '50%',
        marginLeft: '8px',
      }}
    />
  )}
</NavLink>

<NavLink
  to="/thewire"
  onClick={closeSidebar}
  className={({ isActive }) =>
    isActive
      ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
      : 'hover:bg-gray-700 pl-2 py-1'
  }
>
  The Wire
</NavLink>

<NavLink
  to="/news"
  onClick={closeSidebar}
  className={({ isActive }) =>
    isActive
      ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
      : 'hover:bg-gray-700 pl-2 py-1'
  }
>
  News
</NavLink>

</>
    )}

       <div>
  <NavLink
    to="/my-account"
    onClick={closeSidebar}
    className={({ isActive }) =>
      isActive
        ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
        : 'hover:bg-gray-700 pl-2 py-1'
    }
  >
    My Account
  </NavLink>
  {hasReminderPending && (
    <div className="text-yellow-400 text-xs ml-2">
      App Reminder
    </div>
  )}
  {accountSuspended && (
  <div className="text-yellow-400 text-xs ml-2">
    Account Suspended
  </div>
)}
</div>

           <button
  onClick={() => setShowLogoutModal(true)}
  className="text-red-400 mt-4 hover:underline text-left"
>
  Logout
</button>

          </>
        ) : (
          <>
            <NavLink
              to="/login"
              onClick={closeSidebar}
              className={({ isActive }) =>
                isActive
                  ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
                  : 'hover:bg-gray-700 pl-2 py-1'
              }
            >
              Login
            </NavLink>
            <NavLink
              to="/register"
              onClick={closeSidebar}
              className={({ isActive }) =>
                isActive
                  ? 'bg-gray-700 border-l-4 border-blue-400 pl-2 py-1'
                  : 'hover:bg-gray-700 pl-2 py-1'
              }
            >
              Register
            </NavLink>
          </>
        )}
      </div>
    <div className="relative min-h-screen bg-gray-100">
      {/* Hamburger button */}
      {!hideHamburger && (
      <button
  onClick={toggleSidebar}
  className="sidebar-hamburger fixed top-9 left-4 z-50 bg-gray-800 text-white px-2 py-6 rounded-md focus:outline-none relative flex items-center justify-center"
>
    <div className="flex flex-col justify-between h-5">
    <span className="block h-1 w-5 bg-white rounded"></span>
    <span className="block h-1 w-5 bg-white rounded"></span>
    <span className="block h-1 w-5 bg-white rounded"></span>
  </div>

  {(hasUnreadMessages || hasUnreadBolos) && (
    <span
      style={{
        position: 'absolute',
        top: '2px',
        right: '2px',
        width: '10px',
        height: '10px',
        backgroundColor: 'gold',
        borderRadius: '50%',
      }}
    />
  )}
      </button>
      )}

     

      {/* Page content */}
      <div className="px-4">{children}</div>
      {/* Logout Confirmation Modal */}
{showLogoutModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg p-6 w-72 animate-fadeIn">
      <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">
        Logout?
      </h2>
      <p className="text-gray-600 text-sm mb-6 text-center">
        Are you sure you want to log out?
      </p>

      <div className="flex justify-between">
        <button
          onClick={() => setShowLogoutModal(false)}
          className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
        >
          Cancel
        </button>

        <button
          onClick={() => {
            setShowLogoutModal(false);
            handleLogout();
          }}
          className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
        >
          Logout
        </button>
      </div>
    </div>
  </div>
)}

    </div>
</>
  );
}
